<template>
  <section class="mt-10" data-testid="notification-feed">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h2>{{ t('Dashboard.notifications.title') }}</h2>
      <RouterLink
        v-if="showViewAll"
        class="btn-outline-neutral-xs"
        data-testid="notification-view-all"
        :to="{ name: 'notifications' }"
        @click="trackViewAll"
      >
        {{ t('Dashboard.notifications.viewAll') }}
        <ArrowRightIcon class="lucide-icon-s" />
      </RouterLink>
    </div>

    <div class="mt-5 flex flex-col gap-3">
      <div v-if="!loading && !unreadNotifications.length" class="rounded-2xl border border-main bg-surface">
        <NotificationEmptyState />
      </div>

      <NotificationRowTransition>
        <div v-for="row in rows" :key="row.key">
          <h3 v-if="row.type === 'bucket'" class="txt-label-s-medium text-subtlest" data-testid="notification-feed-bucket-header">
            {{ t(`Dashboard.notifications.buckets.${row.bucketKey}`) }}
          </h3>
          <NotificationCard
            v-else
            :notification="row.notification"
            show-read-toggle
            :show-archive="false"
            @cta-click="handleCtaClick"
            @toggle-read="toggleRead"
          />
        </div>
      </NotificationRowTransition>

      <template v-if="loading && skeletonCount">
        <NotificationCardSkeleton v-for="i in skeletonCount" :key="`skeleton-${i}`" single-action />
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ArrowRightIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { useNotificationCenter } from '@/composables/useNotificationCenter'

import NotificationCardSkeleton from './NotificationCardSkeleton.vue'
import NotificationEmptyState from './NotificationEmptyState.vue'
import { notificationRows } from './notificationRows'
import NotificationRowTransition from './NotificationRowTransition.vue'
import NotificationCard from '../NotificationCard/NotificationCard.vue'

const SKELETON_COUNT = 3

const { t } = useI18n()

const { notifications, loading, toggleRead, handleCtaClick, trackViewAll } = useNotificationCenter({
  refetchOnLocaleChange: true,
})

const { enabled: isNotificationCenterEnabled } = useFeatureFlag(FEATURE_FLAG.NOTIFICATIONS.CENTER_RELEASE)

const unreadNotifications = computed(() => notifications.value.filter((item) => !item.readAt))
const rows = computed(() => notificationRows(unreadNotifications.value))
const skeletonCount = computed(() => Math.max(0, SKELETON_COUNT - unreadNotifications.value.length))
const showViewAll = computed(() => isNotificationCenterEnabled.value && unreadNotifications.value.length > 0)
</script>
